import { ListBoxItem } from 'react-aria-components';
import { css, type Theme } from '@emotion/react';
import { SealCheck, User, Tag as TagIcon, CircleHalf, Circle } from '@phosphor-icons/react';
import { Stack } from 'client/components/Stack';
import { Tag } from 'client/components/Tag';
import { Typography } from 'client/components/Typography';
import { ComponentIcon } from 'client/sections/components/ComponentIcon';
import { applyEffect } from 'client/Theme';

const layerToIcon = {
  frontend: <CircleHalf size={14} weight="fill" style={{ rotate: '180deg' }} />,
  backend: <CircleHalf size={14} weight="fill" />,
  fullstack: <Circle size={14} weight="fill" />,
};

const layerToTitle = {
  frontend: 'Front-end',
  backend: 'Backend',
  fullstack: 'Full stack',
};

export interface StackComponentSelectItemProps {
  componentID: number;
  identifier: string;
  name: string;
  description: string;
  versions: string[];
  features?: string[];
  layer?: 'frontend' | 'backend' | 'fullstack';
  owner: {
    name: string;
    isVerified: boolean;
  };
  url: string;
  components?: {
    name: string;
    identifier: string;
    package: string;
  }[];
}

export const StackComponentSelectItem = ({
  name,
  description,
  versions,
  layer,
  features = [],
  owner,
  url,
  components,
}: StackComponentSelectItemProps) => {
  const versionCount = Math.max(0, versions.length - 1);

  return (
    <ListBoxItem id={url} css={itemStyle}>
      <ComponentIcon url={`${url}#main`} type={components ? 'stack' : 'component'} />
      <section>
        <Typography variant="body1" noWrap>
          {name}
        </Typography>
        <Typography variant="body2">{description}</Typography>
        <Typography variant="body2" component="ul">
          {features.map((feature, index) => (
            <li key={index}>
              <span>{feature}</span>
            </li>
          ))}
        </Typography>
        <Stack horizontal gap={8}>
          {layer && (
            <Typography variant="body2">
              {layerToIcon[layer]}
              {layerToTitle[layer]}
            </Typography>
          )}
          <Typography variant="body2">
            <TagIcon size={14} /> {[...versions].pop()}
            {versionCount > 1 && <Tag type="grey">+{versionCount}</Tag>}
          </Typography>
          <Typography variant="body2">
            {owner.isVerified ? <SealCheck weight="fill" size={16} color="var(--primary-color)" /> : <User weight="bold" size={16} />}{' '}
            {owner.name}
          </Typography>
        </Stack>
      </section>
    </ListBoxItem>
  );
};

export const circleEmptyIconURI = (theme: Theme) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' ` +
  `fill='${theme.color.grey[80].replace('#', '%23')}' viewBox='0 0 256 256'%3E%3Cpath d='M208,28H48A20,` +
  `20,0,0,0,28,48V208a20,20,0,0,0,20,20H208a20,20,0,0,0,20-20V48A20,` +
  `20,0,0,0,208,28Zm-4,176H52V52H204Z'%3E%3C/path%3E%3C/svg%3E")`;

export const circleCheckIconURI = (theme: Theme) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' ` +
  `fill='${theme.color.primary[100].replace('#', '%23')}' viewBox='0 0 256 256'%3E%3Cpath d='M208,32H48A16,16,0,0,0,32,` +
  `48V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V48A16,16,0,0,0,208,32Zm-34.34,77.66-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,` +
  `11.32-11.32L112,148.69l50.34-50.35a8,8,0,0,1,11.32,11.32Z'%3E%3C/path%3E%3C/svg%3E")`;

export const itemStyle = (theme: Theme) => css`
  position: relative;
  display: flex;
  align-items: flex-start;
  padding: 16px;
  min-height: 76px;
  background-color: ${theme.color.grey[60]};
  border: none;
  border-radius: ${theme.borderRadius}px;
  overflow: hidden;
  cursor: pointer;
  z-index: 1;

  --primary-color: ${theme.color.primary[100]};

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 16px;
    right: 16px;
    width: 24px;
    height: 24px;
    background-image: ${circleEmptyIconURI(theme)};
    background-size: 100%;
  }

  &:focus-visible {
    outline: solid 4px ${theme.color.primary[100]};
    outline-offset: 4px;
    z-index: 2;
  }

  &:hover:not(:disabled):not([data-selected='true']) {
    background-color: ${applyEffect(theme.color.grey[60], 'hover')};
  }

  &[data-selected='true'] {
    background-color: ${theme.color.primary[60]};

    &::before {
      content: '';
      position: absolute;
      display: block;
      left: -4px;
      width: 8px;
      height: calc(100% - 32px);
      border-radius: 8px;
      background-color: ${theme.color.primary[100]};
    }

    &::after {
      background-image: ${circleCheckIconURI(theme)};
    }
  }

  > figure {
    margin-top: 8px;
    align-self: flex-start;
  }

  > section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-left: 16px;

    > .body1 {
      font-weight: bold;
      color: ${theme.color.foreground};
    }

    ul {
      margin: 0;
      margin-top: 6px;
      padding: 0;
      padding-left: 13px;
      width: 100%;

      > li > span {
        display: block;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }
  }

  .stack {
    margin-top: 6px;

    > p {
      display: flex;
      align-items: center;
      gap: 3px;
    }
  }
`;
